<template>
  <div class="race-track">
    <div class="track-header">
      <div v-if="currentRound" class="lap-badge">
        <span class="lap-badge__icon">🏁</span>
        <span>{{ formatLapLabel(currentRound.roundNumber) }} {{ currentRound.distance }}m</span>
      </div>
      <div v-else class="lap-badge lap-badge--idle">
        <span>Waiting for race...</span>
      </div>
      <div class="finish-marker">FINISH</div>
    </div>
    
    <div class="lanes-container">
      <div 
        v-for="(horse, i) in currentHorses" 
        :key="horse.id" 
        class="lane"
      >
        <span class="lane__number">{{ i + 1 }}</span>
        <div class="lane__track">
          <div class="lane__start-line"></div>
          <div 
            class="horse"
            :style="getHorsePosition(horse)"
          >
            <svg 
              class="horse__icon"
              viewBox="0 0 64 64" 
              xmlns="http://www.w3.org/2000/svg" 
              width="42" 
              height="42"
            >
              <g id="SVGRepo_iconCarrier"> 
                <path fill="#ffdd67" d="M30.2 12.5l1.5-1.9l2.8 6.6l-4.3 1.8z"></path> 
                <path d="M28.6 16.7c1.6.3 1.6 2.3 1.6 2.3c1.7-.7 1.5-4.6.7-6.7l-2.3 4.4" fill="#eba352"></path> 
                <path d="M28 3.4l-6.5 5.8c-.2 2.2.8 2.8 1.1 3.6c.1.3-.3 1.2-.5 1.7c-.5 1.1 1.1 1.1 1.6.8c.6.6-.1 1.5 1.9.1c0 0-1.9 2-.1 1.7c.2 1 .8 2.8 4.8-1c1.2-1.1.7-3.8.7-3.8l2.2-1.4L28 3.4" fill="#ffdd67"></path> 
                <path d="M22.3 4.9c4.3-3.7 8.1-2.3 9.1-1.3c2.5 2.5 2.9 6.4 1.6 9.9c0 0-1.3 0-2.1-1.3c0 0 1.5 0 .9-2.8c-.5-2.4-3.3-2.6-3.2.9l-.3.1C27 9.3 27.4 6.8 25.4 7c-1.8.1-4 2.6-4 2.6c-.9-.7-.9-3.1.9-4.7" fill="#ffb300"></path> 
                <path d="M24.4 12c.2.5.1 1-.2 1.1c-.3.1-.6-.2-.8-.7c-.2-.5-.3-.9.1-1c.3-.2.7.1.9.6" fill="#937237"></path> 
                <path d="M29.5 10.6c-.4-.9.1-2.1.8-2.4c0 0-.9 1.7.1 3c0 0-.7 0-.9-.6" fill="#eba352"></path> 
                <path d="M26.2 14.8s-.6 1.1-.9 1.4c-.5.6-.7.5-.5 0c.2-.4.7-.9.7-.9l.7-.5" fill="#937237"></path> 
                <g fill="#3e4347"> 
                  <path d="M31.8 13.7l-.9.5L27 8.6l.9-.5z"></path> 
                  <path d="M31.6 14l-1-.2l1.7-4.6l1 .2z"></path> 
                  <path d="M30.445 13.163l1.273-.262l.747 3.624l-1.274.262z"></path> 
                </g> 
                <path d="M35.2 9.6c-.1 1.4-4.7-.5-7.4-.6c-3-.1-7.5 1.1-7.4 0c.2-4 3.6-7.1 7.7-6.9c4 .1 7.2 3.5 7.1 7.5" fill="#f15744"></path> 
                <path :fill="horse.color.hex" d="M30.3 18.8l-2.9 5l-1.1.4l.6 1l2.6-.2l2.8-5.6z"></path> 
                <path d="M40.2 23.2s-7.3 5.6-7.8 6.2c-.5.6-.8 1.1-1.2 2.5l2.9 1.3c0-.1 6.3-8.5 6.1-10" fill="#94989b"></path> 
                <g fill="#89664c"> 
                  <path d="M60.8 32.3c2.6-4-7.9-6.1-12.1-.4v1.3s8.6-2.5 6.6 2.4c-2.2 5.4 6.6 10 6.6 10s-4.7-7.6-1.1-13.3"></path> 
                  <path d="M9.2 12.3s-.7-1.5-.3-4.2c0 0 2 1.4 3.4 4.5l-3.1-.3"></path> 
                </g> 
                <path d="M12.7 59.1l.6-4.4s-.9 0-1.8-.2c-.3.8-.6 2.2-.9 3.9c1.1.6 2.1.7 2.1.7" fill="#574137"></path> 
                <path d="M27.2 48.1c-.7.5-1.5 1.2-2.1 1.8l-3.5-1s3.2-4.8 10.7-8c0 0-8.5-3.1-11.4 1c0 0-3.8 5.7-5.1 7c-1.3 1.3.1 2.6.7 2.8c.6.2 9.7 1 9.7 1s1.2-1 2.8-1.6c-.6-1.7-1.3-2.6-1.8-3" fill="#89664c"></path> 
                <path d="M28.5 47.1s-.6.4-1.4 1c.5.5 1.2 1.4 1.9 2.8c.7-.3 1.6-.5 2.3-.4c.1 0-1.1-2.6-2.8-3.4" fill="#574137"></path> 
                <path d="M38.3 36.4l-.8 5.8s7.9 1.3 11.2.1c0 0 2.9 7.2 8.4 11.7c.4-.7.9-1.9 1.2-3.6c-2.1-1.3-5.4-4.4-6.9-10.8c0 0-6.4.8-13.1-3.2" fill="#89664c"></path> 
                <path d="M57.1 54c.7.6 1.5 1.2 2.3 1.7c0 0 .8-2 .9-4.3c0 0-.8-.2-2-1c-.3 1.7-.8 2.9-1.2 3.6" fill="#574137"></path> 
                <g fill="#b7805c"> 
                  <path d="M44.8 28.1c-8 1-15.7 3-17.1-1.4c-5.8-17.9-16.7-15-16.7-15C9.1 9.4 7.2 9 7.2 9c-.2 2.3.7 4.2.7 4.2c-.6 2.8-5.4 12-5.9 13.2c-.5 1.2 2.9 3.4 2.9 3.4c.7-.1.9-1.3 2.4-1.2c.4 0-.7 1.4-.3 1.6c.8.3 1.7-2 3.4-2.9c6.1-3.3 4.1-5.5 4.1-5.5c2.5 1.7 1.1 5.5 0 7.5c-2.5 4.7.1 11.5.1 11.5c-2.5 1.9-7.9 3-7.9 3c-1.9 9.8 1.5 13.4 3.9 14.7c.3-1.7.6-3.1.9-3.9c-.9-.2-1.9-.8-2.3-1.8c-.7-2.1.7-6.8.7-6.8c6.5.8 10.5-1.2 15.2-2.4c4.6-1.1 9.1-.3 13.6.4c17 3 14.8-17 6.1-15.9"></path> 
                  <path d="M37.7 43.3c4.4 10 14 4.5 14 4.5c-2.2 6.1-.6 10.3.8 12.5c1.2-.6 1.9-2 2.3-2.8c-3.1-3.5-.9-8.1-.3-9.9c.7-2-.3-3.3-.3-3.3c-2.7-.6-4.2 1.2-7.4-2.9c-3.2-4-9.1 1.9-9.1 1.9"></path> 
                </g> 
                <g fill="#574137"> 
                  <path d="M53.9 62c1.5-.1 2-3.5 2-3.5c-.4-.3-.8-.7-1.1-1c-.4.8-1.1 2.2-2.3 2.8c.7 1.1 1.4 1.7 1.4 1.7"></path> 
                  <path d="M34.1 38.2s.3 2.2-1.2 5.2c0 0 1.2 0 2.2.1c0 0 .9-1 1.2-5.1l-2.2-.2"></path> 
                </g> 
                <path d="M28.2 27.6s2.4 2.6.9 4.8c-2 3 4.1 8.5 11 5.4c4.9-2.2.2-7.6 2.4-9.7c-.1.1-8.3 1.9-14.3-.5" fill="#89664c"></path> 
                <g fill="#333"> 
                  <path d="M5.4 25.4l-.5-.5c5.7-5.3 7-7.5 7-7.5l.6.4c0 .1-1.3 2.3-7.1 7.6"></path> 
                  <path d="M13.2 18.4c-.8-1.2-1.8-2.3-2.8-3.3c-1-1-2.1-1.6-2.6-2c0 0-.1.5-.2.8c.4.4 1.4.9 2.3 1.8c1.4 1.4 3.2 3.9 4.8 7.5c.2-.1.5-.6.6-.6c-.7-1.5-1.2-2.9-2.1-4.2"></path> 
                  <path d="M6.8 28.7c.2-.1.7 0 .7 0c-1.2-3.2-3.1-5.3-3.8-5.7l-.3.6c.2.1.5.3.9.7c.6.8 1.8 2.5 2.5 4.4"></path> 
                </g> 
                <g fill="#89664c"> 
                  <path d="M7.3 19s1 .4 1.7-.4s.3-2.9.3-2.9s-1.4 1.1-2 3.3"></path> 
                  <path d="M2.9 26.1s1.3-.2.9 1.4c-.5 1.5-1-.9-.9-1.4"></path> 
                </g> 
                <path d="M21.5 26.2c-3.1-.1-6.6-1.1-8.9-2c-2.5-.9-4.4-1.9-4.4-1.9l-.3.5s1.9 1 4.4 1.9c1.5.6 3 1 4.4 1.3c1.6.4 3.1.6 4.4.6c.2 0 .4-.4.4-.4" fill="#333"></path> 
                <path d="M28.7 24.4s-6 .9-6.7 1.1c0 0 .1.4-.4 1.2l8.5.7l3.7-5.6l6.3 1.4s3 .1 6-2.7c0 0-5.8-6.2-12.8-5.8c0 0-1.1 2.3-3.4 3.4l-1.2 6.3" :fill="horse.color.hex"></path> 
                <path d="M40.2 23.2s-5.8 4.9-7 7.8c-.8 2 1.3 4.9 1.7 5.9c0 0 2.2.2 3.5-1.1c0 0 .1-1.1-1.5-3.7c0 0 5.8-2.1 8.1-4.1c2.3-2 2.9-4 1.2-7.5l-6 2.7" fill="#d0d0d0"></path> 
                <path d="M35.8 42.9s-3.7 2-3.9 2.6c-.2.6.6.9.6.9s3.2.4 6.8-.5l-.9-10.4s-.9 1.2-3.8 1.4l1.2 6" fill="#333"></path> 
                <path d="M22.2 26v-1.1c0-.4-.3-.7-.7-.7H21c-.3 0-.5.2-.6.4c-.7 0-1.6.3-1.6 1.6c0 .2 0 1.4 1.4 1.4c2 0 2 0 2-1.6" fill="#ffdd67"></path> 
                <path d="M29.4 9.2s-9.1 2.7-10.5 2.5c-1.4-.2.8-2.1 1.4-2.6s8.5-.6 9.1.1" fill="#c9150d"></path> 
              </g>
            </svg>
          </div>
          <div class="lane__finish-line"></div>
        </div>
      </div>
      
      <div 
        v-for="n in emptyLaneCount" 
        :key="`empty-${n}`" 
        class="lane lane--empty"
      >
        <span class="lane__number">{{ currentHorses.length + n }}</span>
        <div class="lane__track">
          <div class="lane__start-line"></div>
          <div class="lane__finish-line"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { HORSES_PER_RACE } from '@/constants'
import { getOrdinalSuffix } from '@/utils/helpers'

export default {
  name: 'RaceTrack',
  
  setup() {
    const store = useStore()
    
    const currentRound = computed(() => store.getters['race/currentRoundInfo'])
    const progress = computed(() => store.getters['race/raceProgress'])
    
    const currentHorses = computed(() => {
      if (!currentRound.value) return []
      return currentRound.value.horses
    })
    
    const emptyLaneCount = computed(() => {
      return Math.max(0, HORSES_PER_RACE - currentHorses.value.length)
    })
    
    function getHorsePosition(horse) {
      const p = progress.value[horse.id]
      const pct = p?.position ?? 0
      const off = (pct / 100) * 32
      return { left: `calc(${pct}% - ${off}px)` }
    }
    
    function formatLapLabel(num) {
      return `${num}${getOrdinalSuffix(num)} Lap`
    }
    
    return {
      currentRound,
      currentHorses,
      emptyLaneCount,
      getHorsePosition,
      formatLapLabel
    }
  }
}
</script>

<style scoped>
.race-track {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.track-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-4);
  flex-shrink: 0;
}

.lap-badge {
  background: linear-gradient(135deg, var(--color-primary), var(--color-primary-hover));
  color: #fff;
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius);
  font-weight: 600;
  font-size: 15px;
  box-shadow: var(--shadow-sm);
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.lap-badge--idle {
  background: var(--color-text-muted);
}

.lap-badge__icon {
  font-size: 16px;
}

.finish-marker {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #fff;
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius);
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  box-shadow: var(--shadow-sm);
}

.lanes-container {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  background: linear-gradient(180deg, #fef7ed, #fdf4e7);
  border-radius: var(--radius);
  padding: var(--space-3);
  border: 1px solid #e7d5c0;
}

.lane {
  display: flex;
  align-items: center;
  flex: 1;
  min-height: 36px;
}

.lane__number {
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #84cc16, #65a30d);
  border-radius: var(--radius-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  color: #fff;
  margin-right: var(--space-2);
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
}

.lane__track {
  flex: 1;
  height: 100%;
  background: linear-gradient(180deg, #e8dcc8, #ddd0ba);
  border: 1px solid #c9b99a;
  border-radius: var(--radius-sm);
  position: relative;
  overflow: hidden;
}

.lane__start-line,
.lane__finish-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 4px;
}

.lane__start-line {
  left: 0;
  background: linear-gradient(180deg, #22c55e, #16a34a);
  border-radius: var(--radius-sm) 0 0 var(--radius-sm);
}

.lane__finish-line {
  right: 0;
  background: linear-gradient(180deg, #ef4444, #dc2626);
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
}

.horse {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  pointer-events: none;
  transition: left 100ms linear;
}

.horse__icon {
  display: block;
  filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.3));
  transform: scaleX(-1);
}

.lane--empty .lane__track {
  background: linear-gradient(180deg, #f1e8d8, #ebe2d2);
  opacity: 0.6;
}

.lane--empty .lane__number {
  background: linear-gradient(135deg, #94a3b8, #64748b);
}
</style>
